/**
 * 旧内核颜色兜底
 *
 * 本项目整套配色使用 oklch() 颜色写法，现代浏览器（Chrome / Safari / 微信 / QQ 等）都支持。
 * 但部分 App 内置浏览器（例如百度APP安卓端）内核版本过旧，不认识 oklch()，
 * 会导致整条颜色声明失效：标题隐形、按钮丢色、整页变黑白。
 *
 * 这里做一个极轻量的兜底：只有在内核不支持 oklch 时才启动，
 * 把当前配色换算成所有浏览器都认识的 rgb()，写入 --fb-* 变量，
 * 供 Tailwind 的 var(--fb-xxx, oklch(...)) 兜底写法使用。
 * 支持 oklch 的浏览器会在此处直接返回，不产生任何开销。
 */

const ROLE_NAMES = ['background', 'foreground', 'primary', 'accent', 'secondary'];
const SCALE_STEPS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];

const oklchToRgb = (l: number, c: number, hue: number): [number, number, number] => {
  const h = (hue * Math.PI) / 180;
  const a = c * Math.cos(h);
  const b = c * Math.sin(h);

  const lRoot = l + 0.3963377774 * a + 0.2158037573 * b;
  const mRoot = l - 0.1055613458 * a - 0.0638541728 * b;
  const sRoot = l - 0.0894841775 * a - 1.291485548 * b;

  const lCube = lRoot ** 3;
  const mCube = mRoot ** 3;
  const sCube = sRoot ** 3;

  const rLinear = 4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube;
  const gLinear = -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube;
  const bLinear = -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube;

  const toChannel = (value: number): number => {
    const clamped = Math.min(Math.max(value, 0), 1);
    const gamma = clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
    return Math.round(Math.min(Math.max(gamma, 0), 1) * 255);
  };

  return [toChannel(rLinear), toChannel(gLinear), toChannel(bLinear)];
};

const parseChannels = (raw: string): [number, number, number] | null => {
  const parts = raw.trim().split(/\s+/);
  if (parts.length < 3) return null;
  const l = Number(parts[0]);
  const c = Number(parts[1]);
  const hue = Number(parts[2]);
  if (Number.isNaN(l) || Number.isNaN(c) || Number.isNaN(hue)) return null;
  return [l, c, hue];
};

const buildFallbackColors = (): void => {
  const root = document.documentElement;
  const computed = getComputedStyle(root);

  ROLE_NAMES.forEach((role) => {
    SCALE_STEPS.forEach((step) => {
      const token = `${role}-${step}`;
      const channels = parseChannels(computed.getPropertyValue(`--${token}`));
      if (!channels) return;
      const [r, g, b] = oklchToRgb(channels[0], channels[1], channels[2]);
      root.style.setProperty(`--fb-${token}`, `rgb(${r}, ${g}, ${b})`);
    });
  });
};

export const initOklchFallback = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const supportsOklch =
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('color', 'oklch(0.7 0.19 45)');

  if (supportsOklch) return;

  buildFallbackColors();

  const observer = new MutationObserver(() => buildFallbackColors());
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
};